import { Badge, Text, Tooltip } from '@optiaxiom/react';
import { IconAgent, IconCodeBranch } from '@optimizely/axiom-icons';
import { createColumnHelper } from '@tanstack/react-table';

import { UnifiedAgent } from '@frontend/types/agent';

import { ActionsCell, DateCell, NameCell } from './cells';

const columnHelper = createColumnHelper<UnifiedAgent>();

const AgentTypeIcon = {
  specialized: <IconAgent />,
  workflow: <IconCodeBranch />,
};

type CreateAgentColumnsProps = {
  onClickAgent: (agent: UnifiedAgent) => void;
  onCopyAgent: (agent: UnifiedAgent) => void;
  onExportAgent: (agent: UnifiedAgent) => void;
  onPublishAgent?: (agent: UnifiedAgent) => void;
  onDeleteAgent: (agent: UnifiedAgent) => void;
  showPublishOption: boolean;
};

const createColumns = ({
  onClickAgent,
  onCopyAgent,
  onExportAgent,
  onPublishAgent,
  onDeleteAgent,
  showPublishOption,
}: CreateAgentColumnsProps) => [
  columnHelper.accessor('agent_type', {
    id: 'type',
    header: 'Type',
    size: 50,
    cell: ({ row }) => {
      const agentType = row.original.agent_type;
      return (
        <Tooltip content={agentType} textTransform="capitalize">
          <Text mt="4">{AgentTypeIcon[agentType]}</Text>
        </Tooltip>
      );
    },
  }),
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Name',
    minSize: 200,
    cell: ({ row }) => <NameCell agent={row.original} onClick={onClickAgent} />,
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: 'Description',
    size: 450,
    cell: ({ row }) => (
      <Text fontSize="sm" color="fg.tertiary">
        {row.original.description || 'No description'}
      </Text>
    ),
  }),
  columnHelper.accessor('created_at', {
    id: 'created_at',
    header: 'Created',
    size: 80,
    cell: ({ row }) => <DateCell date={row.original.created_at} />,
  }),
  columnHelper.accessor('updated_at', {
    id: 'updated_at',
    header: 'Last Modified',
    size: 80,
    cell: ({ row }) => <DateCell date={row.original.updated_at} />,
  }),
  columnHelper.accessor('is_active', {
    id: 'status',
    header: 'Status',
    size: 80,
    cell: ({ row }) => (
      <Badge truncate intent={row.original.is_active ? 'success' : 'neutral'}>
        {row.original.is_active ? 'Active' : 'Inactive'}
      </Badge>
    ),
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 80,
    enableResizing: false,
    cell: ({ row }) => (
      <ActionsCell
        agent={row.original}
        onCopyAgent={onCopyAgent}
        onExportAgent={onExportAgent}
        onEditAgent={onClickAgent}
        onPublishAgent={onPublishAgent}
        onDeleteAgent={onDeleteAgent}
        showPublishOption={showPublishOption}
      />
    ),
  }),
];

export default createColumns;
